import React, { useEffect, useState } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Hero from "src/components/Hero/Hero";
import DrawCard from "src/components/PromoDrawsCard/DrawCard";
import TitleSection from "src/components/TitleSection/TitleSection";
import {
  getPromoDrawsScheduleDates,
  shouldPublish,
} from "src/utils/publishUtils";
import {
  getPromotionTranslationKey,
  sortPromotionsByDisplayOrder
} from "src/utils/promoUtils";
import styled from "styled-components";
import { DateTime } from "luxon";
import { darkCardStyle } from "src/GlobalStyle";
import { useAccount } from "src/context/account/account.provider";
import Markdown from "src/components/Markdown";
import promoDrawsScheduleConfig from "src/config/promoDrawsScheduleConfig";

const now = DateTime.now();

const PromoDrawsPage = () => {
  const { t, i18n } = useTranslation();
  const { pokerPromosSummary, pokerPromosSummaryFn } = useAccount();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPromoDraws = async () => {
      try {
        // Get the published promo draws from the config
        const publishedPromoDraws = Object.entries(promoDrawsScheduleConfig)
          .filter(([_, config]) => shouldPublish(config))
          .map(([promotionId, config]) => ({
            promotionId,
            key: config.key
          }));

        // console.log("publishedPromoDraws:", publishedPromoDraws);

        // Extract the promotion IDs
        const promotionIds = publishedPromoDraws.map(promo => promo.promotionId);

        // console.log("promotionIds:", promotionIds);

        // Only call the API if there are promotion IDs
        if (promotionIds.length > 0) {
          await pokerPromosSummaryFn(promotionIds);
        } else {
          console.log("No published promo draws found");
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };
    fetchPromoDraws();
  }, [i18n.language]);

  useEffect(() => {
    if (
      Array.isArray(pokerPromosSummary) ||
      pokerPromosSummary?.type === "error"
    ) {
      setLoading(false);
    }
  }, [pokerPromosSummary]);

  return (
    <DrawsSection>
      <Hero
        mainHeroText={t("promoDrawsPage.mainHeroText")}
        description={t("promoDrawsPage.heroDescription")}
        bgImage={"about/about-us-hero-bg_v2.jpg"}
        darkenBg={true}
        className="mb-5"
      />
      <container-div>
        {/* <TitleSection
          dark={false}
          as={"h2"}
          title={t("promoDrawsPage.contentTitle")}
          text={t("promoDrawsPage.contentDescription")}
        /> */}
        {loading ? (
          <EmptyCard className="p-3 mx-auto">
            <p className="pb-3">{t("promoDrawsPage.loadingPromos")}</p>
            <Spinner animation="border" variant="light" />
          </EmptyCard>
        ) : Array.isArray(pokerPromosSummary) && pokerPromosSummary.length > 0 ? (
          <Row xs={1} sm={1} md={2} lg={3}>
            {sortPromotionsByDisplayOrder(pokerPromosSummary).map((draw) => {
              // Get the translation key for this promotion
              const friendlyKey = getPromotionTranslationKey(draw, promoDrawsScheduleConfig);

              // If no translation key was found, log a warning
              if (!friendlyKey) {
                console.warn(`No translation key found for promotion: ${draw?.promotion_id}`);
              }

              const promoData = friendlyKey ? t(`promoDrawsPage.promos.${friendlyKey}`, {
                returnObjects: true,
              }) : {};

              return (
                <CardCol key={draw?.promotion_id}>
                  <DrawCard draw={draw} promoData={promoData} />
                </CardCol>
              );
            })}
          </Row>
        ) : (
          <EmptyCard className="p-3 mx-auto">
            <h3 className="pb-3">{t("promoDrawsPage.noDraws")}</h3>
            <Markdown>{t("promoDrawsPage.noDrawsDescription")}</Markdown>
          </EmptyCard>
        )}
      </container-div>
    </DrawsSection>
  );
};

const DrawsSection = styled.section`
  background: black;
  color: white;
  text-align: center;
  padding: 3rem 0;
  .main-hero-text h1 {
    @media screen and (max-width: 768px) {
      font-size: 2rem;
    }
  }
`;

const CardCol = styled(Col)`
  margin: 0 auto;
`;

const EmptyCard = styled.div`
  ${darkCardStyle}
  max-width: 500px;
`;

export default PromoDrawsPage;
